import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

export const useSkillSheetMainIndex = () => {
  const [searchParams] = useSearchParams();

  // ?company_user_id=8 とか ?user_id=41 を拾う
  const searchParamsString = searchParams.toString();
  const searchString = searchParamsString ? `?${searchParamsString}` : "";

  const [workHistoryGroups, setWorkHistoryGroups] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const [page, setPage] = useState(1);

  // 常時表示されるチェックボックスの選択状態
  const [selectedWorkHistoryIds, setSelectedWorkHistoryIds] = useState([]);

  // 確認モーダルの表示状態と、実行対象の情報
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null,
    groupId: null,
    workHistoryId: null,
  });

  const selectedCount = selectedWorkHistoryIds.length;

  // 2件以上でグループ化可能
  const canCreateGroup = selectedCount >= 2 && !grouping;

  // ボタン非活性条件
  const isDisabled = !canCreateGroup;

  const getCsrfToken = () =>
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";

  const fetchWorkHistories = async (targetPage = page, signal) => {
    setLoading(true);

    try {
      // page がある場合は &page=
      // query が空なら ?page=
      const connector = searchString ? "&" : "?";

      const res = await fetch(
        `/skill_sheet_mains.json${searchString}${connector}page=${targetPage}`,
        {
          signal,
        },
      );

      if (!res.ok) throw new Error();

      const data = await res.json();
      const currentPage = data.pagination?.current_page;

      setWorkHistoryGroups(data.workHistoryGroups || []);
      setPagination(data.pagination);

      if (typeof currentPage === "number" && currentPage !== targetPage) {
        setPage(currentPage);
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("取得に失敗しました。");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    fetchWorkHistories(page, controller.signal);

    return () => controller.abort();
  }, [page, searchString]);

  const goToPage = (p) => {
    window.scrollTo({ top: 0 });
    setPage(p);
  };

  // ボタン押下時にグループ化を実行
  const handleGroupAction = () => {
    handleGroupSelected();
  };

  const handleGroupSelected = async () => {
    if (!canCreateGroup) return;

    setGrouping(true);

    try {
      const res = await fetch(`/skill_sheet_mains/group${searchString}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({
          work_history_ids: selectedWorkHistoryIds,
        }),
      });

      if (!res.ok) throw new Error();

      toast.success("グループ化しました。");

      // 選択状態をリセット
      setSelectedWorkHistoryIds([]);

      await fetchWorkHistories(page);
    } catch {
      toast.error("失敗しました。");
    } finally {
      setGrouping(false);
    }
  };

  // 確認モーダルを閉じ、対象情報もリセットする
  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      type: null,
      groupId: null,
      workHistoryId: null,
    });
  };

  // グループから外す前に確認モーダルを表示する
  const handleRemoveFromGroup = ({ groupId, workHistoryId }) => {
    setConfirmModal({
      open: true,
      type: "remove",
      groupId,
      workHistoryId,
    });
  };

  // グループ解除前に確認モーダルを表示する
  const handleUngroup = (groupId) => {
    setConfirmModal({
      open: true,
      type: "ungroup",
      groupId,
      workHistoryId: null,
    });
  };

  // 確認モーダルで「確定」したときに、選択された処理を実行する
  const handleConfirmGroupAction = async () => {
    const { type, groupId, workHistoryId } = confirmModal;

    closeConfirmModal();

    setGrouping(true);

    try {
      if (type === "remove") {
        const res = await fetch(
          `/skill_sheet_mains/groups/${groupId}/remove${searchString}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": getCsrfToken(),
            },
            body: JSON.stringify({
              work_history_id: workHistoryId,
            }),
          },
        );

        if (!res.ok) throw new Error();

        toast.success("グループから外しました。");
      }

      if (type === "ungroup") {
        const res = await fetch(
          `/skill_sheet_mains/groups/${groupId}/ungroup${searchString}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": getCsrfToken(),
            },
          },
        );

        if (!res.ok) throw new Error();

        toast.success("グループを解除しました。");
      }

      await fetchWorkHistories(page);
    } catch {
      toast.error(
        type === "remove"
          ? "グループから外せませんでした。"
          : "グループを解除できませんでした。",
      );
    } finally {
      setGrouping(false);
    }
  };

  const noticeText = (() => {
    if (selectedCount > 0) {
      return `${selectedCount}件選択中です。2件以上でグループ化できます。`;
    }

    return "同じプロジェクトを選択してください。";
  })();

  const actionLabel = grouping ? "処理中..." : "グループを作成";

  const confirmModalText = (() => {
    if (confirmModal.type === "remove") {
      return {
        title: "グループから外しますか？",
        message: "このプロジェクトをグループから外します。",
        confirmLabel: "外す",
      };
    }

    return {
      title: "グループを解除しますか？",
      message: "このグループを解除します。",
      confirmLabel: "解除する",
    };
  })();

  return {
    workHistoryGroups,
    pagination,
    loading,
    grouping,
    page,
    selectedWorkHistoryIds,
    setSelectedWorkHistoryIds,
    confirmModal,
    selectedCount,
    isDisabled,
    noticeText,
    actionLabel,
    confirmModalText,
    goToPage,
    handleGroupAction,
    closeConfirmModal,
    handleRemoveFromGroup,
    handleUngroup,
    handleConfirmGroupAction,
  };
};
